import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Building2, UserPlus } from 'lucide-react';

export default function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invite, setInvite] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    api.get(`/invitations/${token}`)
      .then(r => { setInvite(r.data); setLoading(false); })
      .catch(() => { setError('Invitacion invalida o expirada'); setLoading(false); });
  }, [token]);

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/auth/register-invite', { token, name, password });
      navigate('/');
      window.location.reload();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al aceptar la invitacion');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563EB]" />
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-4">
        <Card className="w-full max-w-md border-[#E2E8F0] shadow-sm">
          <CardContent className="p-8 text-center">
            <div className="h-12 w-12 rounded-full bg-[#FEE2E2] flex items-center justify-center mx-auto mb-4">
              <UserPlus size={24} className="text-[#EF4444]" />
            </div>
            <h2 className="text-xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Invitacion no valida</h2>
            <p className="text-sm text-[#475569] mb-4">{error}</p>
            <Button onClick={() => navigate('/login')} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white min-h-11 md:min-h-10 focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2">Ir al inicio</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (user && typeof user === 'object') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-4">
        <Card className="w-full max-w-md border-[#E2E8F0] shadow-sm">
          <CardContent className="p-8 text-center">
            <div className="h-12 w-12 rounded-full bg-[#EFF6FF] flex items-center justify-center mx-auto mb-4">
              <Building2 size={24} className="text-[#2563EB]" />
            </div>
            <h2 className="text-xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Unirse a {invite?.org_name}</h2>
            <p className="text-sm text-[#475569] mb-4">Ya tienes sesion iniciada como {user.email}. Acepta la invitacion para unirte a la organizacion.</p>
            <Button onClick={handleAccept} disabled={saving} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white w-full min-h-11 md:min-h-10 focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2" data-testid="accept-invite-btn">
              {saving ? 'Procesando...' : 'Aceptar invitacion'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-4" data-testid="invite-page">
      <Card className="w-full max-w-md border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-2">
          <div className="h-12 w-12 rounded-full bg-[#EFF6FF] flex items-center justify-center mb-3">
            <Building2 size={24} className="text-[#2563EB]" />
          </div>
          <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Te han invitado a {invite?.org_name}
          </h2>
          <p className="text-sm text-[#475569]">
            Crea tu cuenta para unirte como <span className="font-medium">{invite?.role === 'admin' ? 'Administrador' : 'Usuario'}</span>
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAccept} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Correo electronico</Label>
              <Input value={invite?.email || ''} disabled className="min-h-11 md:min-h-10 border-[#E2E8F0] bg-[#F8FAFC]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Nombre completo</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Tu nombre" className="min-h-11 md:min-h-10 border-[#E2E8F0]" data-testid="invite-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Contrasena</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Crea una contrasena" className="min-h-11 md:min-h-10 border-[#E2E8F0]" data-testid="invite-password-input" />
            </div>
            {error && <div className="text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#EF4444]/20 rounded-md px-3 py-2">{error}</div>}
            <Button type="submit" disabled={saving || !name || !password} className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold min-h-11 md:min-h-10 focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2" data-testid="invite-submit-btn">
              {saving ? 'Creando cuenta...' : 'Crear cuenta y unirse'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
